import { createDebugger } from '../utils/debug.js';
import { initLazyMedia } from '../utils/helpers/lazy-media.js';
import { CustomLightbox } from '../utils/helpers/lightbox-custom.js';
import '../utils/resize-event.js';

// HLS.js for HLS video support - dynamically imported when needed
let Hls = null;

const debug = createDebugger('responsive-video', false, false);

const LOAD_TYPES = {
    DOM: 'dom',
    LOAD: 'load',
    MANUAL: 'manual'
};

const VIDEO_ERROR_CODES = {
    MEDIA_ERR_ABORTED: 1,
    MEDIA_ERR_NETWORK: 2,
    MEDIA_ERR_DECODE: 3,
    MEDIA_ERR_SRC_NOT_SUPPORTED: 4
};

const SELECTORS = {
    VIDEO: 'video',
    SOURCES_TEMPLATE: 'template[data-sources]',
    RESPONSIVE_VIDEO_WRAPPER: '.responsive-video-wrapper',
    POSTER_IMAGE: '.poster-image',
    PLAY_TOGGLE: '[data-play-toggle]',
    SOUND_TOGGLE: '[data-sound-toggle]',
    ICON_PLAY: '[data-icon-play]',
    ICON_SOUND_ON: '[data-icon-sound-on]',
    ICON_SOUND_OFF: '[data-icon-sound-off]'
};

class ResponsiveVideo extends HTMLElement {
    constructor() {
        super();
        this.onClick = this.onClick.bind(this);
        this.onResize = this.onResize.bind(this);
        this.onLoaded = this.onLoaded.bind(this);
        this.onVideoEvent = this.onVideoEvent.bind(this);
    }

    connectedCallback() {
        debug.log('connectedCallback: Initializing responsive-video component');

        this.lazyType = this.getAttribute('data-lazy-type');
        this.hasNativeControls = this.hasAttribute('data-native-controls');
        this.isLightboxEnabled = this.hasAttribute('data-enable-lightbox');

        debug.log('Component config:', {
            lazyType: this.lazyType,
            hasNativeControls: this.hasNativeControls,
            isLightboxEnabled: this.isLightboxEnabled
        });

        this.initElements();

        this.sources = this.getSources();
        debug.log('Found video sources:', this.sources);

        this.video.style.opacity = '0';

        // If lightbox is enabled, initialize it but still setup video for inline playback
        if (this.isLightboxEnabled) {
            debug.log('ResponsiveVideo has lightbox enabled:', this.isLightboxEnabled);
            debug.log('ResponsiveVideo data attributes:', this.dataset);
            this.initLightbox();
            // Set up resize listener for responsive sources
            this.setupResponsiveSourceListener();
        }

        this.setupEventListeners();
        document.addEventListener('theme:resize', this.onResize);
        this.initVideo();
    }

    initLightbox() {
        const parentSection = this.closest('.shopify-section, [id^="shopify-section-"]');
        const sectionId = parentSection ? parentSection.id : 'unknown-section';
        debug.log('initLightbox: Starting lightbox initialization', { sectionId });

        try {
            this.lightbox = new CustomLightbox({
                childSelector: `#${sectionId} responsive-video[data-enable-lightbox]`,
                gallerySelector: `#${sectionId}`,
                mainClass: 'pswp--product-lightbox',
                addListener: true,
                debug: true
            });

            this.lightbox.init();
            debug.log('initLightbox: Lightbox initialized successfully');
        } catch (error) {
            debug.error('initLightbox: Failed to initialize lightbox', error);
        }
    }

    initVideo() {
        this.updateSource();

        const execute = () => {
            if (this.lazyType) {
                this.video.setAttribute('data-src', this.currentSource);
                initLazyMedia();
            } else {
                this.load();
            }
        };

        if (this.isVisible()) {
            execute();
        } else {
            this.observeVisibility(execute);
        }
    }

    isVisible() {
        const rect = this.getBoundingClientRect();
        return rect.width > 0 && rect.height > 0;
    }

    observeVisibility(callback) {
        const observer = new IntersectionObserver((entries) => {
            entries.forEach((entry) => {
                if (entry.isIntersecting && this.isVisible()) {
                    // Element is now visible
                    callback();
                    observer.disconnect();
                }
            });
        });

        observer.observe(this);
    }

    initElements() {
        this.video = this.querySelector(SELECTORS.VIDEO);
        this.sourcesTemplate = this.querySelector(SELECTORS.SOURCES_TEMPLATE);
        if (!this.video) return;

        this.poster = this.querySelector(SELECTORS.POSTER_IMAGE);
        this.videoWrapper = this.querySelector(SELECTORS.RESPONSIVE_VIDEO_WRAPPER);
        this.soundBtn = this.querySelector(SELECTORS.SOUND_TOGGLE);
        this.playToggle = this.querySelector(SELECTORS.PLAY_TOGGLE);

        this.playIcon = this.querySelector(SELECTORS.ICON_PLAY);
        this.soundOnIcon = this.querySelector(SELECTORS.ICON_SOUND_ON);
        this.soundOffIcon = this.querySelector(SELECTORS.ICON_SOUND_OFF);

        // If lightbox is enabled, prevent all video interactions
        if (this.isLightboxEnabled && this.video) {
            this.video.style.pointerEvents = 'none';
        }
    }

    getSources() {
        return Array.from(this.sourcesTemplate.content.querySelectorAll('source')).map((s) => ({
            src: s.getAttribute('data-src'),
            media: s.media,
            type: s.type
        }));
    }

    cleanSourceURL(src) {
        if (src.startsWith('//')) {
            src = `${window.location.protocol}${src}`;
        }

        return new URL(src, window.location.href).href;
    }

    updateSource() {
        // Find media query matches, prioritizing MP4 over other formats
        const mediaMatches = this.sources.filter((s) => s.media && matchMedia(s.media).matches);
        const fallbacks = this.sources.filter((s) => !s.media);

        debug.log('updateSource: Media query matches', {
            mediaMatches,
            fallbacks,
            windowWidth: window.innerWidth
        });

        // Prioritize MP4 sources within each category
        const match = mediaMatches.find((s) => s.type === 'video/mp4') || mediaMatches[0];
        const fallback = fallbacks.find((s) => s.type === 'video/mp4') || fallbacks[0];

        const nextSrc = (match || fallback || this.sources[0]).src;

        if (!nextSrc) {
            debug.warn('updateSource: No source found');
            return;
        }

        const newSourceUrl = this.cleanSourceURL(nextSrc);

        if (this.video.src) {
            const currentSourceUrl = this.cleanSourceURL(this.video.src);
            if (currentSourceUrl === newSourceUrl) {
                debug.log('updateSource: Source unchanged, skipping update');
                return;
            }
        }

        this.currentSource = nextSrc;
        debug.log('updateSource: New source selected', { currentSource: this.currentSource });
    }

    getCurrentResponsiveSource() {
        // Get the appropriate video source based on current viewport width
        const width = window.innerWidth;
        const videoData = {
            src: this.dataset.pswpVideo,
            width: parseInt(this.dataset.pswpWidth, 10),
            height: parseInt(this.dataset.pswpHeight, 10)
        };

        // Check for mobile source (max-width: 719px)
        if (width <= 719 && this.dataset.pswpVideoMobile) {
            videoData.src = this.dataset.pswpVideoMobile;
            videoData.width = parseInt(this.dataset.pswpWidthMobile, 10) || videoData.width;
            videoData.height = parseInt(this.dataset.pswpHeightMobile, 10) || videoData.height;
        }
        // Check for large source (min-width: 1800px)
        else if (width >= 1800 && this.dataset.pswpVideoLarge) {
            videoData.src = this.dataset.pswpVideoLarge;
            videoData.width = parseInt(this.dataset.pswpWidthLarge, 10) || videoData.width;
            videoData.height = parseInt(this.dataset.pswpHeightLarge, 10) || videoData.height;
        }

        return videoData;
    }

    setupResponsiveSourceListener() {
        // Store the last known breakpoint
        this.lastBreakpoint = this.getCurrentBreakpoint();

        // Bind handler for cleanup
        this._breakpointChangeHandler = () => {
            const currentBreakpoint = this.getCurrentBreakpoint();
            if (currentBreakpoint !== this.lastBreakpoint) {
                this.lastBreakpoint = currentBreakpoint;
                // Dispatch custom event for lightbox to update
                this.dispatchEvent(
                    new CustomEvent('responsive-source-change', {
                        detail: this.getCurrentResponsiveSource(),
                        bubbles: true
                    })
                );
            }
        };

        // Listen to global debounced resize event
        document.addEventListener('theme:resize', this._breakpointChangeHandler);
    }

    getCurrentBreakpoint() {
        const width = window.innerWidth;
        if (width <= 719) return 'mobile';
        if (width >= 1800) return 'large';
        return 'desktop';
    }

    async load() {
        debug.log('load: Starting video load', { currentSource: this.currentSource });

        // Check if current source is HLS and browser doesn't support it natively
        const isHLS = this.currentSource && this.currentSource.includes('.m3u8');
        const supportsHLS =
            this.video.canPlayType('application/x-mpegURL') ||
            this.video.canPlayType('application/vnd.apple.mpegurl');

        debug.log('load: Video format check', {
            isHLS,
            supportsHLS,
            userAgent: navigator.userAgent
        });

        if (isHLS && !supportsHLS) {
            debug.log('load: HLS source detected, loading HLS.js');
            // Try to load HLS.js for non-Safari browsers
            await this.loadHLS();
            if (Hls && Hls.isSupported()) {
                debug.log('load: HLS.js loaded and supported, using HLS playback');
                this.setupHLS();
                return;
            }
            debug.warn('load: HLS.js not supported, falling back to standard sources');
        }

        // Fallback to standard source loading
        debug.log('load: Using standard source loading');
        this.loadStandardSources();
    }

    async loadHLS() {
        if (Hls) {
            debug.log('loadHLS: HLS.js already loaded');
            return;
        }

        debug.log('loadHLS: Attempting to load HLS.js from CDN');
        try {
            // Dynamically import HLS.js from CDN
            const hlsModule = await import('https://cdn.skypack.dev/hls.js@latest');
            Hls = hlsModule.default;
            debug.log('loadHLS: HLS.js loaded successfully');
        } catch (error) {
            debug.error('loadHLS: Failed to load HLS.js', error);
        }
    }

    setupHLS() {
        debug.log('setupHLS: Setting up HLS playback');

        // Clean up existing HLS instance
        if (this.hls) {
            debug.log('setupHLS: Destroying existing HLS instance');
            this.hls.destroy();
        }

        this.hls = new Hls({
            enableWorker: true,
            lowLatencyMode: false
        });

        debug.log('setupHLS: Loading HLS source', { source: this.currentSource });
        this.hls.loadSource(this.currentSource);
        this.hls.attachMedia(this.video);

        this.hls.on(Hls.Events.MANIFEST_PARSED, () => {
            debug.log('setupHLS: HLS manifest parsed, video ready to play');
        });

        this.hls.on(Hls.Events.ERROR, (event, data) => {
            debug.error('setupHLS: HLS error occurred', { event, data });
            if (data.fatal) {
                debug.error('setupHLS: Fatal HLS error, falling back to standard sources');
                // Fallback to standard loading on fatal errors
                this.loadStandardSources();
            }
        });
    }

    loadStandardSources() {
        debug.log('loadStandardSources: Loading standard video sources');

        // Clear existing sources
        const existingSources = this.video.querySelectorAll('source');
        debug.log('loadStandardSources: Clearing existing sources', { count: existingSources.length });
        existingSources.forEach((source) => source.remove());

        // Add all sources from template to video element
        const sources = Array.from(this.sourcesTemplate.content.querySelectorAll('source'));
        debug.log('loadStandardSources: Adding sources from template', { count: sources.length });

        sources.forEach((templateSource) => {
            const source = document.createElement('source');
            source.src = templateSource.getAttribute('data-src');
            source.type = templateSource.type;
            if (templateSource.media) {
                source.media = templateSource.media;
            }
            this.video.appendChild(source);
            debug.log('loadStandardSources: Added source', {
                src: source.src,
                type: source.type,
                media: source.media
            });
        });

        // Set fallback src for browsers that don't support source elements
        this.video.src = this.currentSource;
        debug.log('loadStandardSources: Set fallback src', { src: this.currentSource });

        // Force the video to reload with new sources
        this.video.load();
        debug.log('loadStandardSources: Video load() called');
    }

    onResize(event) {
        const width = event?.detail?.width || window.innerWidth;
        debug.log('onResize: Checking for source changes', { windowWidth: width });

        const previousSource = this.currentSource;
        this.updateSource();

        // Only reload if source actually changed
        if (previousSource !== this.currentSource) {
            debug.log('onResize: Source changed, reloading video', {
                previousSource,
                newSource: this.currentSource
            });
            this.load();
            const playing = !this.video.paused;
            if (playing) {
                debug.log('onResize: Resuming playback after reload');
                this.video.play().catch((error) => {
                    debug.error('onResize: Failed to resume playback', error);
                });
            }
        } else {
            debug.log('onResize: Source unchanged, skipping reload');
        }
    }

    setupEventListeners() {
        this.video.addEventListener('loadeddata', this.onLoaded);

        // Skip all interactive event listeners if lightbox is enabled
        if (this.isLightboxEnabled) {
            return;
        }

        if (this.hasNativeControls) {
            // For native controls, only listen to video events for UI updates
            this.video.addEventListener('play', this.onVideoEvent);
            this.video.addEventListener('pause', this.onVideoEvent);
            this.video.addEventListener('volumechange', this.onVideoEvent);
        } else {
            // Add general click handler to video wrapper for play/pause
            this.videoWrapper.addEventListener('click', this.onClick);
        }

        // For custom controls, setup click handlers
        if (this.playToggle) {
            this.playToggle.addEventListener('click', (e) => {
                e.stopPropagation();
                this.togglePlay();
            });
        }
        if (this.soundBtn) {
            this.soundBtn.addEventListener('click', (e) => {
                e.stopPropagation();
                this.toggleSound();
            });
        }
    }

    onClick(e) {
        // Only handle general clicks if not native controls and not clicking on custom controls
        if (this.hasNativeControls) return;

        if (e.target.closest('[data-sound-toggle]') || e.target.closest('[data-play-toggle]')) {
            return; // Let specific handlers deal with these
        }

        this.togglePlay();
    }

    onVideoEvent() {
        // Update UI based on video state for native controls
        this.updateControlsUI();
    }

    togglePlay() {
        const action = this.video.paused ? 'play' : 'pause';
        debug.log(`togglePlay: ${action}ing video`);

        if (this.video.paused) {
            this.video.play().catch((error) => {
                debug.error('togglePlay: Failed to play video', error);
            });
        } else {
            this.video.pause();
        }

        this.updateControlsUI();
    }

    toggleSound() {
        const newMutedState = !this.video.muted;
        debug.log('toggleSound: Toggling sound', { wasMuted: this.video.muted, nowMuted: newMutedState });

        this.video.muted = newMutedState;
        this.updateControlsUI();
    }

    onLoaded() {
        debug.log('onLoaded: Video loaded', {
            hasAutoplay: this.video.hasAttribute('autoplay'),
            readyState: this.video.readyState,
            duration: this.video.duration
        });

        if (this.video.hasAttribute('autoplay')) {
            debug.log('onLoaded: Autoplay detected, starting playback (muted)');
            this.video.muted = true;
            this.video.play().catch((error) => {
                debug.error('onLoaded: Autoplay failed', error);
            });
        }

        this.updateControlsUI();
    }

    updateControlsUI() {
        if (!this.posterHidden && !this.video.paused) {
            this.poster.style.opacity = '0';
            this.video.style.opacity = '1';

            this.posterHidden = true;
        }

        if (this.playIcon) {
            this.playIcon.classList.toggle('hidden', !this.video.paused);
        }
        if (this.soundOnIcon && this.soundOffIcon) {
            this.soundOnIcon.classList.toggle('hidden', this.video.muted);
            this.soundOffIcon.classList.toggle('hidden', !this.video.muted);
        }
    }

    disconnectedCallback() {
        debug.log('disconnectedCallback: Cleaning up responsive-video component');

        // Clean up HLS instance
        if (this.hls) {
            debug.log('disconnectedCallback: Destroying HLS instance');
            this.hls.destroy();
            this.hls = null;
        }

        if (this.videoWrapper) {
            this.videoWrapper.removeEventListener('click', this.onClick);
        }
        if (this.playToggle) {
            this.playToggle.removeEventListener('click', this.togglePlay);
        }
        if (this.soundBtn) {
            this.soundBtn.removeEventListener('click', this.toggleSound);
        }
        if (this.video) {
            this.video.removeEventListener('loadeddata', this.onLoaded);
            this.video.removeEventListener('play', this.onVideoEvent);
            this.video.removeEventListener('pause', this.onVideoEvent);
            this.video.removeEventListener('volumechange', this.onVideoEvent);
        }
        document.removeEventListener('theme:resize', this.onResize);
        // Clean up breakpoint change listener
        if (this._breakpointChangeHandler) {
            document.removeEventListener('theme:resize', this._breakpointChangeHandler);
        }

        debug.log('disconnectedCallback: Cleanup complete');
    }
}

customElements.define('responsive-video', ResponsiveVideo);
